import React, { useState } from 'react';

const EducationInquiryForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    program: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.message.length > 500) {
      setSubmitMessage('Message exceeds the maximum length of 500 characters.');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('program', formData.program);
      formBody.append('message', formData.message);

      const response = await fetch(
        'https://readdy.ai/api/form/d6akdg5t1k61917cms50',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: formBody.toString(),
        }
      );

      if (response.ok) {
        setSubmitMessage('Thank you for your inquiry. We will be in touch shortly.');
        setFormData({ name: '', email: '', program: '', message: '' });
      } else {
        setSubmitMessage('Something went wrong. Please try again.');
      }
    } catch {
      setSubmitMessage('Network error. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <section
      id="education-inquiry-section"
      className="py-24 px-6 lg:px-12 bg-[#faf8f5]"
    >
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-10 h-[1px] bg-bronze/40" />
            <span className="text-bronze text-[8px] font-sans font-semibold tracking-[0.45em] uppercase whitespace-nowrap">
              Inquire
            </span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <h2 className="font-display text-4xl md:text-[3rem] font-light text-charcoal italic leading-[1.1]">
              Get in Touch
            </h2>
            <p className="text-textGray/60 text-[13px] font-sans font-light leading-[2] max-w-lg">
              Whether you are a practitioner exploring professional training or an individual curious about our educational offerings — we welcome your inquiry.
            </p>
          </div>
        </div>

        {/* Full-width form */}
        <div className="bg-warmWhite rounded-2xl p-10 shadow-sm border border-warmGray/10">
          <form
            id="education-inquiry-form"
            onSubmit={handleSubmit}
            className="space-y-6"
            data-readdy-form
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-[9px] font-sans font-semibold tracking-[0.3em] uppercase text-textGray/40 mb-2">Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Your full name"
                  className="w-full px-0 py-3 border-0 border-b border-warmGray/30 bg-transparent focus:outline-none focus:border-bronze/60 transition-colors text-sm font-sans text-charcoal placeholder:text-lightGray/60"
                />
              </div>
              <div>
                <label className="block text-[9px] font-sans font-semibold tracking-[0.3em] uppercase text-textGray/40 mb-2">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="your@email.com"
                  className="w-full px-0 py-3 border-0 border-b border-warmGray/30 bg-transparent focus:outline-none focus:border-bronze/60 transition-colors text-sm font-sans text-charcoal placeholder:text-lightGray/60"
                />
              </div>
              <div>
                <label className="block text-[9px] font-sans font-semibold tracking-[0.3em] uppercase text-textGray/40 mb-2">Program of Interest</label>
                <select
                  name="program"
                  value={formData.program}
                  onChange={handleChange}
                  required
                  className="w-full px-0 py-3 border-0 border-b border-warmGray/30 bg-transparent focus:outline-none focus:border-bronze/60 transition-colors text-sm font-sans text-charcoal cursor-pointer appearance-none"
                  style={{ backgroundImage: 'none' }}
                >
                  <option value="">Select a program</option>
                  <option value="Stability Masterclass">Stability Masterclass</option>
                  <option value="Method Training Program">Method Training Program</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-[9px] font-sans font-semibold tracking-[0.3em] uppercase text-textGray/40 mb-2">Message <span className="normal-case tracking-normal font-light">(optional)</span></label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                maxLength={500}
                rows={4}
                placeholder="Tell us a little about your background or what you are looking for..."
                className="w-full px-0 py-3 border-0 border-b border-warmGray/30 bg-transparent focus:outline-none focus:border-bronze/60 transition-colors text-sm font-sans text-charcoal resize-none placeholder:text-lightGray/60"
              />
              <p className="text-right text-[10px] text-textGray/30 mt-1">{formData.message.length}/500</p>
            </div>

            <div className="pt-2 flex justify-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-16 py-4 bg-charcoal text-white rounded-full text-[11px] font-sans font-semibold tracking-wider uppercase hover:bg-charcoal/85 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
              </button>
            </div>

            {submitMessage && (
              <div
                className={`p-4 rounded-xl text-center ${
                  submitMessage.includes('Thank you') ? 'bg-cream text-charcoal' : 'bg-red-50 text-red-600'
                }`}
              >
                <p className="text-xs font-sans">{submitMessage}</p>
              </div>
            )}
          </form>
        </div>

      </div>
    </section>
  );
};

export default EducationInquiryForm;
